import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';

const navLinks = [
  { label: '首页', path: '/' },
  { label: '电视剧', path: '/category/dianshiju' },
  { label: '电影', path: '/category/dianying' },
  { label: '动漫', path: '/category/dongman' },
  { label: '综艺', path: '/category/zongyi' },
  { label: '纪录片', path: '/category/jilupian' },
  { label: '关于我们', path: '/about' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const handleSearch = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setSearchOpen(false);
      setSearchQuery('');
    }
  }, [searchQuery]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <nav className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 flex-shrink-0">
            <span className="w-9 h-9 flex items-center justify-center bg-primary-500 rounded-lg">
              <i className="ri-play-fill text-background-50 text-xl"></i>
            </span>
            <span className="text-xl font-heading font-bold text-foreground-950 whitespace-nowrap">
              人人影视
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  location.pathname === link.path
                    ? 'bg-primary-500 text-background-50'
                    : 'text-foreground-700 hover:text-foreground-950 hover:bg-background-200/70'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-3">
            {searchOpen ? (
              <form onSubmit={handleSearch} className="flex items-center">
                <div className="relative">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="搜索影视..."
                    className="w-48 pl-9 pr-4 py-2 text-sm rounded-full bg-background-100 border border-background-200/70 
                      text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300/50 
                      focus:border-primary-300 transition-all duration-200"
                    autoFocus
                  />
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
                </div>
                <button
                  type="button"
                  onClick={() => setSearchOpen(false)}
                  className="ml-2 w-8 h-8 flex items-center justify-center rounded-full text-foreground-500 hover:text-foreground-700 hover:bg-background-200/70 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line"></i>
                </button>
              </form>
            ) : (
              <button
                onClick={() => setSearchOpen(true)}
                className="w-9 h-9 flex items-center justify-center rounded-full text-foreground-600 hover:text-foreground-900 hover:bg-background-200/70 transition-colors cursor-pointer"
                aria-label="搜索"
              >
                <i className="ri-search-line text-lg"></i>
              </button>
            )}
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-full text-foreground-700 hover:bg-background-200/70 transition-colors cursor-pointer"
            aria-label="菜单"
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </nav>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70">
          <div className="px-4 py-4 flex flex-col gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                  location.pathname === link.path
                    ? 'bg-primary-500 text-background-50'
                    : 'text-foreground-700 hover:bg-background-100'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <form onSubmit={handleSearch} className="mt-2">
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="搜索影视..."
                  className="w-full pl-10 pr-4 py-3 text-sm rounded-lg bg-background-100 border border-background-200/70 
                    text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300/50"
                />
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
              </div>
            </form>
          </div>
        </div>
      )}
    </header>
  );
}